import { IconNames } from '@dynamic/ui';
import { t } from 'i18next';
import React, { FC, useRef } from 'react';
import styled from 'styled-components';

import { horizontalDirection } from '#/service/constants';
import { noop, remify } from '#/service/utils';

import {
  Container,
  cssInputBase,
  InputLabel,
  StyledIcon,
} from '../FormInput.styled';
import { CommonInputProps, InputHandlerProps } from '../FormInput.types';
import { ErrorMessage } from './ErrorMessage';

interface InputSelectProps extends CommonInputProps, InputHandlerProps {
  highlighted?: boolean;
  icon?: IconNames;
  inputValue?: string;
  values: { key: string; value: string }[];
}

interface SelectHeaderProps {
  $hasIcon: boolean;
  $highlighted: boolean;
  $touched?: boolean;
  errorMessage?: string;
}

export const InputContainer = styled.div`
  position: relative;
  height: ${remify(50)};
  width: 100%;
  outline: none !important;
  background-color: var(--inputBackground);
  border-radius: ${remify(8)};
`;

const SelectHeader = styled.select<SelectHeaderProps>`
  ${({ $highlighted, $hasIcon, theme }) =>
    cssInputBase($highlighted, $hasIcon, theme)}
  cursor: pointer;
  border: none;
  appearance: none;
  border-radius: ${remify(8)};
  position: relative;
  z-index: 1;
  background-color: transparent;

  &:invalid {
    ${({ $touched }) =>
      $touched && `box-shadow: inset 0 0 0 2px var(--inputValidation);`}
  }

  &:focus {
    outline: 1px solid var(--itemFocusFrame);
    outline-offset: 2px;
  }
`;

const InputSelect: FC<InputSelectProps> = ({
  dataKey,
  errorMessages,
  handleBlur = noop,
  handleFocus = noop,
  handleMouseEnter = noop,
  handleMouseLeave = noop,
  icon,
  inputValue,
  highlighted = false,
  label = '',
  required,
  updateErrorMessages = noop,
  updateInputValue = noop,
  touched,
  values,
}) => {
  const inputRef = useRef<HTMLSelectElement>(null);

  const validate = () => {
    const message = inputRef.current?.validationMessage;

    updateErrorMessages(dataKey, message);
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    updateInputValue(dataKey, { value: e.target.value });
  };

  const handleSelectBlur = () => {
    handleBlur();
    validate();
  };

  return (
    <Container>
      <InputContainer
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <InputLabel
          $bigLabel={!inputValue}
          $hasIcon={!!icon}
          $highlighted={highlighted}
          htmlFor={dataKey}
        >
          {t(label)}
        </InputLabel>
        {icon && (
          <StyledIcon
            $highlighted={highlighted}
            aria-hidden="true"
            name={icon}
            position={horizontalDirection.LEFT}
          />
        )}
        <SelectHeader
          $hasIcon={!!icon}
          $highlighted={highlighted}
          $touched={touched}
          id={dataKey}
          onBlur={handleSelectBlur}
          onChange={handleSelectChange}
          onFocus={handleFocus}
          ref={inputRef}
          required={required}
          value={inputValue ?? ''}
        >
          <option disabled value="" />
          {values.map(({ key, value }) => (
            <option key={key} value={key}>
              {value}
            </option>
          ))}
        </SelectHeader>
        <StyledIcon
          $highlighted={highlighted}
          name="chevron_down"
          position={horizontalDirection.RIGHT}
        />
      </InputContainer>
      {errorMessages?.map((errorMessage) => (
        <ErrorMessage key={errorMessage}>{errorMessage}</ErrorMessage>
      ))}
    </Container>
  );
};

export default InputSelect;
